import React from 'react';
import ExpandedSection from './ExpandedSection';
import { normalizePublicLandingUrl } from '../publicLandingUrl';
import { toTestIdPart } from '../../utils/testIds';
import './productOverviewSection.css';
import type { PublicCoaFiles } from '../../hooks/usePublicCoaResults';
import { formatDate } from '../../utils/formatDate';

type OverviewRow = {
  label: string;
  value: string;
  mono?: boolean;
};

type ProductOverviewSectionProps = {
  viewCoaLeftSrc: string;
  viewCoaRightSrc: string;
  passedIconSrc: string;
  rows?: OverviewRow[];
  labTestStatus?: string;
  coaFiles?: string[];
  coaResults?: PublicCoaFiles;
  showOverview?: boolean;
  showBatchResults?: boolean;
  showTable?: boolean;
};

const COA_VISIBLE = 3;

export default function ProductOverviewSection({
  viewCoaLeftSrc,
  viewCoaRightSrc,
  passedIconSrc,
  rows = [],
  labTestStatus,
  coaFiles = [],
  coaResults,
  showOverview = true,
  showBatchResults = true,
  showTable = true,
}: ProductOverviewSectionProps) {
  const [expanded, setExpanded] = React.useState(false);
  const fileListId = React.useId();
  const safeCoaFiles = coaResults
    ? coaResults.files
    : coaFiles
      .map(normalizePublicLandingUrl)
      .filter((file): file is string => file !== null)
      .map((url) => ({ url, testerName: '', testPerformedDate: '' }));
  const files = !expanded && safeCoaFiles.length > COA_VISIBLE ? safeCoaFiles.slice(0, COA_VISIBLE) : safeCoaFiles;
  const overflow = safeCoaFiles.length - COA_VISIBLE;
  const unavailableMetadata = coaResults?.isLoading ? 'Loading...' : 'Not provided';

  return (
    <ExpandedSection cardClassName="rid-product-overview">
      {showBatchResults && (coaResults?.error ? (
        <div role="alert">
          <p>{coaResults.error}</p>
          <button type="button" className="rid-coa-tray-control" onClick={coaResults.retry}>Retry COAs</button>
        </div>
      ) : coaResults?.isLoading ? (
        <p role="status">Loading COAs...</p>
      ) : coaResults && safeCoaFiles.length === 0 ? (
        <p role="status">No COA available</p>
      ) : null)}
      {showBatchResults && safeCoaFiles.length > 0 && (
        <>
          <div id={fileListId} role="group" aria-label="Certificates of analysis" className="rid-coa-chip-row" data-testid="landing-overview-coa-files">
            {files.map((file, i) => (
              <a key={file.url} href={file.url} target="_blank" rel="noopener noreferrer"
                data-testid={`landing-overview-coa-file-${i + 1}`}
                className={safeCoaFiles.length === 1 ? 'rid-product-overview__coa-btn' : 'rid-coa-chip'}>
                <img src={viewCoaLeftSrc} alt="" aria-hidden="true" />
                <span className="rid-coa-chip-content">
                  <span>{safeCoaFiles.length === 1 ? 'View COA' : `COA ${i + 1}`}</span>
                  {coaResults && (
                    <>
                      <span>Laboratory: <span data-testid={`landing-overview-coa-lab-${i + 1}`}>{file.testerName || unavailableMetadata}</span></span>
                      <span>Tested date: <span data-testid={`landing-overview-coa-date-${i + 1}`}>{file.testPerformedDate ? formatDate(file.testPerformedDate) : unavailableMetadata}</span></span>
                      <span className="rid-coa-new-tab-notice">Opens PDF in a new tab</span>
                    </>
                  )}
                </span>
                <img src={viewCoaRightSrc} alt="" aria-hidden="true" />
              </a>
            ))}
          </div>
          {overflow > 0 && (
            <button type="button" className="rid-coa-see-more rid-coa-tray-control" onClick={() => setExpanded(!expanded)} data-testid="landing-overview-coa-expand" data-expanded={expanded} aria-expanded={expanded} aria-controls={fileListId}>
              {expanded ? 'See less' : `+${overflow} more`}
            </button>
          )}
        </>
      )}

      {showTable && <table className="rid-product-overview__table" data-testid={showOverview ? 'landing-product-overview-table' : 'landing-batch-results-summary-table'}>
        <thead>
          <tr>
            <th>{showOverview ? 'Product Overview' : 'Batch Test Results'}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(showOverview ? rows : []).map((row) => {
            const rowId = toTestIdPart(row.label, 'row');
            return (
              <tr key={row.label} data-testid={`landing-product-overview-row-${rowId}`}>
                <td data-testid={`landing-product-overview-label-${rowId}`}>{row.label}</td>
                <td className={row.mono ? 'rid-product-overview__mono' : undefined} data-testid={`landing-product-overview-value-${rowId}`}>{row.value}</td>
              </tr>
            );
          })}
          {showBatchResults && labTestStatus && (
            <tr data-testid="landing-product-overview-row-lab-test-status">
              <td>Lab Test Status</td>
              <td className="rid-product-overview__status-pass-inline" data-testid="landing-product-overview-value-lab-test-status">
                {labTestStatus.toUpperCase() === 'PASSED' && (
                  <img src={passedIconSrc} alt="" aria-hidden="true" />
                )}
                {labTestStatus}
              </td>
            </tr>
          )}
        </tbody>
      </table>}
    </ExpandedSection>
  );
}
